import React, { useState } from 'react';
import { getRecommandation, poserRdvFiche } from '../services/api';
import { jourLocal } from '../services/dates';

// La date E-RDV de Zoho arrive en « 23-10-2026 09:00:00+02:00 ». Les deux
// lecteurs vivent aussi dans la page Planification RDV : ils sont courts et
// les dupliquer vaut mieux qu'un module de plus pour six lignes.
function parseErdv(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  return { day: m[1], month: m[2], year: m[3], hour: m[4], minute: m[5] };
}

function fmtErdv(str) {
  const p = parseErdv(str);
  if (!p) return null;
  const d = new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00`);
  if (isNaN(d)) return null;
  const auj = new Date(); auj.setHours(0, 0, 0, 0);
  return {
    date: d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
    heure: `${p.hour}h${p.minute}`,
    iso: `${p.year}-${p.month}-${p.day}`,
    passee: d < auj,
  };
}

/**
 * Date conseillee pour une commande sans E-RDV.
 *
 * La conduite d'activite cherchait un creneau a la main. Le moteur le lui
 * designe, avec la raison : une date nue ne se verifie pas, « deja dans la
 * commune ce jour-la » se comprend d'un coup d'oeil.
 *
 * L'appel est a la demande et non au chargement : chaque proposition interroge
 * Zoho sur une vingtaine de journees, et une page en affiche plusieurs dizaines.
 */
/**
 * `onChoisir` recoit { date, creneau, technicien } : le volet s'en sert pour
 * remplir la confirmation. Sans lui, le composant se contente d'afficher —
 * la conduite d'activite recopiait le creneau a la main.
 *
 * `dossierId` ajoute a chaque ligne de quoi poser le rendez-vous tout de
 * suite : la date, l'heure et le technicien, puis l'etape « Planifiee ». Sans
 * lui, il fallait relever le creneau a l'ecran et le ressaisir dans trois
 * champs, dans le bon ordre, avec le risque de basculer l'etape avant la date
 * — et la confirmation partait alors au client sans rendez-vous dedans.
 */
function PropositionDate({ itv, onChoisir, dossierId, onPose }) {
  const [etat, setEtat] = useState('repos');   // repos | chargement | ok | vide | erreur
  const [data, setData] = useState(null);
  const [msg, setMsg]   = useState(null);

  // La pose : la ligne en cours d'ecriture, le resultat, et ce qui l'a empeche.
  const [enCours, setEnCours] = useState(null);
  const [pose, setPose] = useState(null);
  const [erreurPose, setErreurPose] = useState(null);
  // Six propositions suffisent a decider, mais pas a choisir : le reste se
  // deplie sur demande, sans rappeler le moteur.
  const [tout, setTout] = useState(false);
  const PREMIERES = 6;

  const cle = p => p.date + '|' + p.creneau + '|' + p.technicien;

  const poser = async (p, confirme) => {
    setEnCours(cle(p)); setErreurPose(null);
    try {
      const r = await poserRdvFiche(dossierId, {
        date: p.date, creneau: p.creneau, heure: p.heure || undefined,
        technicien: p.technicien, confirme: !!confirme,
      });
      const t = String(r.ecrits.Technicien_OT || '').replace('VALEA-', '');
      setPose(`Posé le ${p.date.split('-').reverse().join('/')} à `
        + `${r.ecrits.Heure_de_l_intervention} avec ${r.technicien_nom || t} — étape `
        + `« Planifiée ». La confirmation part au client et à Covage.`);
      if (onPose) onPose();
    } catch (e) {
      const rep = e.response?.data;
      // Le serveur refuse une premiere fois quand la pose coute quelque chose
      // ailleurs : un creneau reserve par un autre client, ou la date que ce
      // client-la avait choisie. On nomme ce qui se passe, et on n'insiste que
      // si la conduite d'activite le veut.
      if (rep?.confirmation_requise) {
        setEnCours(null);
        if (window.confirm('Poser ce rendez-vous :\n\n' + rep.error
            + '\n\nContinuer quand même ?')) return poser(p, true);
        setErreurPose('Rendez-vous non posé.');
        return;
      }
      setErreurPose(rep?.error || rep?.erreur || 'Pose impossible');
    } finally { setEnCours(null); }
  };

  // Une date E-RDV encore valable est le choix du client. Le bloc se cachait
  // alors entierement, et la conduite d'activite ne pouvait plus rien chercher
  // sur un dossier qui porte une date — meme quand cette date doit changer.
  // On la dit, et on laisse chercher.
  const erdv = itv.date_erdv ? fmtErdv(itv.date_erdv) : null;
  if (!itv.code_postal) return null;

  // Une date d'intervention DEPASSEE n'est pas une planification : c'est celle
  // du work order precedent, que l'import ne remet pas a zero. Le bouton se
  // cachait ainsi sur seize dossiers « A planifier » sur vingt, et la
  // conduite d'activite a cru la fonction disparue.
  const aujourdhui = jourLocal(new Date());
  const datePosee = String(itv.date_intervention || '').slice(0, 10);
  const dejaPlanifie = datePosee && datePosee >= aujourdhui;
  const datePerimee = datePosee && datePosee < aujourdhui ? datePosee : null;
  const jjmm = iso => String(iso || '').slice(0, 10).split('-').reverse().join('/');

  const chercher = async () => {
    setEtat('chargement'); setMsg(null); setTout(false);
    try {
      const d = await getRecommandation({
        // L'identifiant du dossier passe devant : c'est le serveur qui lit sa
        // typologie et son type de déclenchement SAV, et qui choisit le
        // moteur — les créneaux de la grille pour un raccordement, les heures
        // insérables dans la journée du technicien pour un SAV.
        dossier: dossierId || undefined,
        cp: itv.code_postal, typo: itv.typo_raccordement || '', nb: 30,
        // Une commande qui revient garde son technicien : il connaît le site.
        technicien: itv.technicien || undefined,
      });
      setData(d);
      setEtat((d.propositions || []).length ? 'ok' : 'vide');
    } catch (e) {
      setMsg(e.response?.data?.erreur || e.response?.data?.error || 'Recherche impossible');
      setEtat('erreur');
    }
  };

  // Une fois le rendez-vous pose, la liste des creneaux n'a plus d'objet : le
  // dossier est planifie. On dit ce qui a ete ecrit, la page se rafraichit
  // derriere et le bloc disparait de lui-meme.
  if (pose) return (
    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(46,160,67,.12)', border: '1px solid rgba(46,160,67,.45)',
                  fontSize: 12, color: 'var(--green2)', fontWeight: 600, lineHeight: 1.5 }}>
      ✓ {pose}
    </div>
  );

  if (etat === 'repos') return (
    <div style={{ marginTop: 10, display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* « Proposer une date » laissait croire qu'on proposait au client.
          Le bouton ne fait que demander au moteur les creneaux libres. */}
      <button className="btn btn-sm" onClick={chercher}>Trouver une date auto</button>
      {erdv && !erdv.passee && (
        <span style={{ fontSize: 11, color: '#b45309' }}>
          le client a choisi le {erdv.iso.split('-').reverse().join('/')} à {erdv.heure} — ne cherchez
          une autre date que si elle doit changer
        </span>
      )}
      {dejaPlanifie && (
        <span style={{ fontSize: 11, color: '#b45309' }}>
          une intervention est déjà posée le {jjmm(datePosee)}
        </span>
      )}
      {erdv && erdv.passee && (
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>date E-RDV dépassée</span>
      )}
      {datePerimee && (
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          le dossier porte encore le {datePerimee.split('-').reverse().join('/')} du passage précédent
        </span>
      )}
    </div>
  );

  if (etat === 'chargement') return (
    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)' }}>Recherche d'un créneau…</div>
  );

  if (etat === 'erreur') return (
    <div style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>
      {msg} <button className="btn btn-sm" onClick={chercher} style={{ marginLeft: 8 }}>Réessayer</button>
    </div>
  );

  if (etat === 'vide') return (
    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: '#fef3c7', border: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
      {data.message || 'Aucun créneau disponible sur l’horizon.'}
    </div>
  );

  // Le serveur dit quel moteur a repondu : l'ecran n'a pas a le deviner.
  const estSav = data.moteur === 'sav';

  return (
    <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: '#eff6ff', border: '2px solid #bfdbfe' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {estSav ? 'Heures conseillées — SAV' : 'Dates conseillées'}
        </span>
        <span style={{ fontSize: 10, color: '#1d4ed8' }}>
          dept {data.departement} · {data.duree_heures} h · {data.total_trouve} proposition{data.total_trouve > 1 ? 's' : ''}
          {estSav && data.sav && data.sav.limite && ` · délai ${data.sav.type} depuis le ${jjmm(data.sav.depuis)}`
            + ` → à tenir avant le ${jjmm(data.sav.limite)}`}
          {estSav && ` · ${data.total_proches} à proximité dans le délai`}
          {data.technicien_impose && ' · limité à ' + (data.technicien_impose.replace('VALEA-', ''))}
        </span>
        <button className="btn btn-sm" onClick={chercher} style={{ marginLeft: 'auto' }}>Actualiser</button>
      </div>
      {(tout ? data.propositions : data.propositions.slice(0, PREMIERES)).map((p, i) => {
        const d = new Date(p.date + 'T12:00:00');
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '5px 0', borderTop: i ? '1px solid #bfdbfe' : 'none',
          }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#1d4ed8', minWidth: 190 }}>
              {d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })} à {p.creneau}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              {p.technicien_nom || p.technicien.replace('VALEA-', '')}
              <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 5 }}>
                {p.technicien.replace('VALEA-', '')}
              </span>
            </span>
            <span style={{ fontSize: 11, color: p.dans_sla_premiere_inter ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
              {estSav
                ? (data.sav && data.sav.limite
                    ? (p.dans_delai ? 'dans le délai ' + data.sav.type : 'hors délai ' + data.sav.type)
                    : 'sans échéance')
                : <>J+{p.delai_jours_ouvres} j.ouvrés{p.dans_sla_premiere_inter ? '' : ' — hors SLA 15 j'}</>}
            </span>
            {estSav && p.tenable === false && (
              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#92400e', background: '#fef3c7',
                             border: '1px solid #fde68a', borderRadius: 20, padding: '1px 7px' }}>
                ⚠ {p.retard_minutes} min de retard sur le rendez-vous voisin
              </span>
            )}
            {p.raisons.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{p.raisons.join(' ; ')}</span>
            )}
            {onChoisir && (
              // « Choisir » ne disait pas quoi : on a cru que le clic envoyait
              // la proposition au client. Il ne fait que remplir les deux
              // blocs d'en dessous, et rien ne part.
              <button className="btn btn-sm" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                title="Remplit « Proposition de RDV » et « Confirmer → Planifiée » en dessous — n’envoie rien"
                onClick={() => onChoisir({ date: p.date, creneau: p.creneau,
                                           heure: p.heure || null, technicien: p.technicien })}>
                Proposer ce créneau
              </button>
            )}
            {dossierId && (
              <button className="btn btn-sm" disabled={!!enCours}
                title="Pose la date, l’heure et le technicien, puis bascule en « Planifiée »"
                style={{ marginLeft: onChoisir ? 0 : 'auto', background: '#15803d',
                         color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                onClick={() => poser(p, false)}>
                {enCours === cle(p) ? 'Validation…' : 'Valider ce RDV'}
              </button>
            )}
          </div>
        );
      })}
      {data.propositions.length > PREMIERES && (
        <button className="btn btn-sm" onClick={() => setTout(t => !t)}
          style={{ marginTop: 8, background: 'transparent', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
          {tout
            ? 'Ne montrer que les ' + PREMIERES + ' premières'
            : 'Voir les ' + (data.propositions.length - PREMIERES) + ' autres propositions'}
        </button>
      )}
      {data.technicien_demande_hors_zone && (
        <div style={{ fontSize: 10, color: '#b45309', marginTop: 6 }}>
          {data.technicien_demande_hors_zone.replace('VALEA-', '')} est assigné à cette commande mais ne couvre
          pas cette adresse : la recherche a été étendue à toute l’équipe du département.
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
        {estSav
          ? 'Classement : le délai d’abord, puis les heures qu’il peut tenir, puis le technicien le '
            + 'plus proche du client ce jour-là. Les heures proposées se glissent dans sa journée — '
            + 'avant sa première intervention ou entre deux — car un SAV ne prend qu’une heure et '
            + 'n’attend pas un créneau libre. La route indiquée est celle du rendez-vous voisin, '
            + 'celui qu’il doit quitter ou rejoindre. Au plus deux heures par journée et par '
            + 'technicien, pour que la liste montre plusieurs jours. '
          : (data.technicien_impose
              ? 'Technicien conservé car la commande lui est déjà assignée. '
              : 'Classement au plus tôt, puis par trajet déjà fait. ')}
        {onChoisir
          ? '« Proposer ce créneau » ne fait que remplir les blocs « Proposition de RDV » et '
            + '« Confirmer → Planifiée » en dessous : rien ne part avant que vous n’y cochiez l’envoi. '
          : (dossierId ? '' : 'La saisie de la date reste manuelle. ')}
        {dossierId
          && '« Valider ce RDV » est l’autre chemin, celui du client qui a déjà accepté : il pose la date, '
           + 'l’heure et le technicien, puis bascule le dossier en « Planifiée » — la confirmation part alors '
           + 'au client et à Covage. Le créneau est revérifié au clic.'}
      </div>
      {erreurPose && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c', fontWeight: 600, lineHeight: 1.45 }}>
          {erreurPose}
        </div>
      )}
    </div>
  );
}

export default PropositionDate;
